/**
 * Money formatting, matching the landing's ledger conventions exactly.
 *
 * Two deliberate departures from `Intl.NumberFormat(style: "currency")`:
 *
 *  - The shekel sign is prefixed manually. The `he-IL` currency formatter emits
 *    a trailing sign wrapped in bidi control characters, which fights the
 *    surface designs and makes string comparison in tests miserable.
 *  - Negatives render in accounting parentheses — `(₪720)` — not with a minus.
 *    That is what `ProfitabilityPanel.astro` does, and it is the convention
 *    accountants read fastest.
 *
 * Callers must still wrap output in <bdi> when it sits inside Hebrew prose.
 */
const SHEKEL = "₪";

export interface MoneyFormatOptions {
  /** Show agorot. Off by default: ledger columns are whole shekels. */
  decimals?: boolean;
  /** Render negatives as (₪720) rather than ₪-720. */
  accountingNegative?: boolean;
}

export function formatIls(amount: number, options: MoneyFormatOptions = {}): string {
  const { decimals = false, accountingNegative = true } = options;

  const fractionDigits = decimals ? 2 : 0;
  const magnitude = Math.abs(amount).toLocaleString("he-IL", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

  const rendered = `${SHEKEL}${magnitude}`;

  if (amount >= 0) return rendered;
  return accountingNegative ? `(${rendered})` : `-${rendered}`;
}

/** Compact form for dense KPI strips: ₪1.2K, ₪3.4M. */
export function formatIlsCompact(amount: number): string {
  const magnitude = Math.abs(amount).toLocaleString("he-IL", {
    notation: "compact",
    maximumFractionDigits: 1,
  });
  const rendered = `${SHEKEL}${magnitude}`;
  return amount < 0 ? `(${rendered})` : rendered;
}

/**
 * Money crosses the wire as an integer count of agorot.
 *
 * Postgres `numeric` arrives as a string in node-postgres, and turning that into
 * a float to do arithmetic is how rounding errors get into invoices. Convert at
 * the boundary and keep integers in between.
 */
export function agorotToShekels(agorot: number): number {
  return agorot / 100;
}

export function shekelsToAgorot(shekels: number): number {
  return Math.round(shekels * 100);
}

/** Parse a `numeric` column arriving as a string, without a float round-trip. */
export function parseNumericToAgorot(value: string | null | undefined): number {
  if (value == null || value === "") return 0;
  const [whole = "0", fraction = ""] = value.trim().replace(/,/g, "").split(".");
  const negative = whole.startsWith("-");
  const wholeDigits = negative ? whole.slice(1) : whole;
  const agorot =
    Number(wholeDigits || "0") * 100 + Number(fraction.padEnd(2, "0").slice(0, 2) || "0");
  return negative ? -agorot : agorot;
}
